import MutableTrack from './MutableTrack.js';
import {SAMPLED_INSTRUMENT} from 'constants/instrument-types.js';
import {sleep} from 'utils/sleep.js';
import Preset from 'repos/Preset.js';
import ParameterContext from 'effects/ParameterContext.js';
import SoundUploader from 'api/SoundUploader.js';
import {INSTRUMENT} from 'constants/track-types.js';
import Synth from 'instruments/Synth.js';
import {setTrackIsLoading} from 'actions/index.js';

class InstrumentTrack extends MutableTrack {
    constructor(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence, createSynth=true) {
        super(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence);
        this.setupEffects();
        this.instrument = undefined;

        if (createSynth) {
            this.setInstrument(
                new Synth(
                    sequencerEngine,
                    mixSession,
                    new ParameterContext().forTrack(this)));
        };

        this.preset = new Preset();
        this.preset.track = this;
    }

    async auditionNext(shouldDispose, isKeyboard=false) {
        if (this.isFrozen || isKeyboard) {
            return;
        }
        if (this.getInstrumentType() === SAMPLED_INSTRUMENT &&
            !(this.preset && this.preset.contentHash)) {
            await super.auditionNext();
            return;
        }
        let browsed = this.browsedPresets;
        if (!browsed) {
            return;
        }

        if (!this.instrument.loaded) {
            return;
        }
        this.instrument.loaded = false;
        this.trackIsLoading  = true;

        let index = 0;
        if (this.preset && this.preset.contentHash) {
            index = browsed.map(x => x.contentHash).indexOf(this.preset.contentHash);
            index++;
            if (index >= browsed.length) {
                return;
            }
        }
 
        let p = browsed[index];

        // this.mute(true);
        await this.sequencerEngine
            .auditionPreset(p, this.trackNumber, true, true);
        // this.mute(false);
        this.trackIsLoading  = false;
    }

    async auditionPrevious(shouldDispose, isKeyboard) {
        if (this.isFrozen || isKeyboard) {
            return;
        }
        if (this.getInstrumentType() === SAMPLED_INSTRUMENT &&
            !(this.preset && this.preset.contentHash)) {
            await super.auditionPrevious();
            return;
        }

        let browsed = this.browsedPresets;
        if (!browsed) {
            return;
        }

        if (!this.instrument.loaded) {
            return;
        }
        this.trackIsLoading = true;

        let index = 0;
        if (this.preset && this.preset.contentHash) {
            index = browsed.map(x => x.contentHash).indexOf(this.preset.contentHash);
            index--;
            if (index < 0) {
                return;
            }
       }
        let p = browsed[index];
        this.mute(true);
        await this.sequencerEngine
            .auditionPreset(p, this.trackNumber, true, true);
        this.mute(false);
        this.trackIsLoading = false;
    }

    setIsEnabled(isEnabled) {
        super.setIsEnabled(isEnabled);
        if (!isEnabled && !this.isFrozen) {
            this.getInstrument().releaseAll();
        }
    }

    async makePublicIfNeeded() {
    }

    dispose(disposeParameters, isFrozen=this.isFrozen) {
        this.midiEffects.dispose();
        if (!isFrozen && this.instrument) {
            this.instrument.dispose();
            this.instrument = undefined;
        }
        super.dispose(!isFrozen);
    }

    getAllParameters() {
        let parameters = super.getAllParameters();
        if (this.isFrozen || this.instrument === undefined) {
            return parameters;
        }
        
        let instrument = this.getInstrument();
        let instrumentParameters = instrument.getParameters();
        return [... parameters, ... instrumentParameters];
    }

    getAllPatternLockedParameters(oldPattern, patternNumber) {
        let parameters = super.getAllPatternLockedParameters(oldPattern, patternNumber);

        if (this.isFrozen) {
            return parameters;
        }

        let instrument = this.getInstrument();
        let instrumentParameters = instrument.getParameters();
        instrumentParameters = instrumentParameters
            .filter(p => p.hasPatternLockedValues(oldPattern, patternNumber));
        return parameters.concat(instrumentParameters);
    }

    getCopy(constr, sequencerEngine=this.sequencerEngine, ignoreId=false) {
        let track = constr(
            this.trackNumber,
            sequencerEngine,
            this.mixSession,
            this.patterns.length,
            this.update,
            sequencerEngine.currentSubSequence);

        if (!this.isFrozen) {
            let instrument = this.instrument.getCopy(
                new ParameterContext().forTrack(track),
                track,
                sequencerEngine,
                ignoreId
            );
            track.setInstrument(instrument);
        }

        super.getCopy(() => track, sequencerEngine, ignoreId);
        return track;
    }

    getName() {
        return this.instrument === undefined ? 'Instrument' :
            this.instrument.getName();
    }

    getTrackType() {
        return INSTRUMENT;
    }

    getInstrument() {
        return this.instrument;
    }
    
    setInstrument(instrument) {
        if (this.instrument !== undefined) {
            this.instrument.disconnect(this.gainNode);
        }
        this.instrument = instrument;

        if (this.instrument !== undefined) {
            this.instrument.connect(this.gainNode);
        }
    }

    liveTrigger(stepData, slice, time=0) {
        if (this.isFrozen) {
            return;
        }
        this.trigger(stepData, time, true);
    }

    liveTriggerRelease(transpose) {
        if (this.isFrozen) {
            return;
        }
        this.triggerRelease(transpose);
    }

    trigger(stepData, time, step) {
        if (this.isFrozen) {
            super.trigger(stepData, time, step);
            return;
        }
        if (this.instrument !== undefined) {
            this.instrument.trigger(stepData, time);
        }
    }

    triggerRelease(transpose) {
        if (this.isFrozen) {
            return;
        }
        if (this.instrument !== undefined) {
            this.instrument.release(transpose);
        }
    }

    getJson(skipFreeze, isPreset) {
        let isFrozen = this.instrumentRack ? this.instrumentRack.isFrozen :
            this.isFrozen;
        return {
            ... super.getJson(skipFreeze),
            instrumentType: this.instrument ? this.instrument.getInstrumentType() : this.getInstrumentType(),
            instrument: isFrozen ? undefined : this.instrument.getJson(isPreset),
            presetName: this.presetName,
            preset: this.preset.getJson()
        };
    }

    async fromJson(json, isFullTrack=true, loadPreset=true, ignoreId=false, isPreset) {
        super.fromJson(json, isPreset);

        this.parametersToScale = undefined;

        if (isFullTrack) {
            this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, true));
       }

        // todo: once there are more instruments, need to check instrument type
        if (json.envelopes) {
            for (let i in json.envelopes) {
                this.connectToEnvelope(
                    this.sequencerEngine.envelopes[json.envelopes[i]]);
            }
        }
        if ('envelopeConnections' in json) {
            for (let i in json.envelopeConnections) {
                let connection = json.envelopeConnections[i];
                this.connectToEnvelope(this.subSequence.envelopes[
                    connection.envelope],
                    connection.mode);
            }
        }
        let bulk = {};
        if (json.isFrozen) {
            // this.instrument.dispose();
        } else {
            if (!this.instrument) {
                this.createNewInstrument();
            }
            bulk = await this.instrument.fromJson(json.instrument, ignoreId, isPreset);
        }

        if (json.presetName !== undefined) {
            this.presetName = json.presetName;
        }

        if (json.preset) {
            if (loadPreset) {
                this.preset.fromJson(json.preset);
                await this.preset.load();
                if (this.preset.name) {
                    this.presetName = this.preset.name;
                }
            }
        }
        if (isFullTrack) {
            this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, false));
        }
        return bulk;
    }

    async unfreezeInstrument(json=this.frozenJson) {
        this.createNewInstrument();
        await this.instrument.fromJson(json.trackParameters.instrument);
    }

    createNewInstrument() {}

    isMonohonic() {
        return this.tracks.every(
            ({track}) => track.isMonophonic());
    }
}

export default InstrumentTrack;
